import React, { useRef, useState, useCallback } from 'react';

interface Props {
  children: React.ReactNode;
  onSwipeRight?: () => void;
  onSwipeLeft?: () => void;
  rightLabel?: string;
  leftLabel?: string;
  rightColor?: string;
  leftColor?: string;
  className?: string;
}

const THRESHOLD = 80;

const SwipeableCard: React.FC<Props> = ({
  children,
  onSwipeRight,
  onSwipeLeft,
  rightLabel = 'Progress',
  leftLabel = 'Message',
  rightColor = '#10b981',
  leftColor = '#0066ff',
  className = '',
}) => {
  const startX = useRef(0);
  const [offsetX, setOffsetX] = useState(0);
  const [swiping, setSwiping] = useState(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    setSwiping(true);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!swiping) return;
    const diff = e.touches[0].clientX - startX.current;
    const clamped = Math.max(-120, Math.min(120, diff));
    setOffsetX(clamped);
  }, [swiping]);

  const handleTouchEnd = useCallback(() => {
    if (offsetX > THRESHOLD && onSwipeRight) {
      onSwipeRight();
    } else if (offsetX < -THRESHOLD && onSwipeLeft) {
      onSwipeLeft();
    }
    setOffsetX(0);
    setSwiping(false);
  }, [offsetX, onSwipeRight, onSwipeLeft]);

  const showRight = offsetX > 30;
  const showLeft = offsetX < -30;

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {showRight && (
        <div
          className="absolute inset-y-0 left-0 flex items-center pl-4 text-xs font-bold uppercase tracking-wider rounded-l-2xl z-0"
          style={{ width: Math.abs(offsetX), background: rightColor + '30', color: rightColor }}
        >
          {rightLabel}
        </div>
      )}
      {showLeft && (
        <div
          className="absolute inset-y-0 right-0 flex items-center justify-end pr-4 text-xs font-bold uppercase tracking-wider rounded-r-2xl z-0"
          style={{ width: Math.abs(offsetX), background: leftColor + '30', color: leftColor }}
        >
          {leftLabel}
        </div>
      )}
      <div
        className="relative z-10 transition-transform touch-pan-y"
        style={{
          transform: swiping ? `translateX(${offsetX}px)` : 'translateX(0)',
          transition: swiping ? 'none' : 'transform 0.2s ease-out',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {children}
      </div>
    </div>
  );
};

export default SwipeableCard;
